import { useState, useEffect } from "react";
import { apiPost, apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";

export default function EngagementReports({ token }) {
  const [sending, setSending] = useState(null);
  const [result, setResult] = useState(null);
  const [archive, setArchive] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);

  const API_BASE = import.meta.env.VITE_API_URL || "https://api.careersorter.co";

  // Load archive on mount
  useEffect(() => {
    loadArchive();
  }, [token]);

  const loadArchive = async () => {
    try {
      const data = await apiGet("/reports/archive", token);
      setArchive(data.reports || []);
    } catch (err) {
      console.error("Failed to load report archive:", err);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleSend = async (period) => {
    setSending(period);
    setResult(null);
    try {
      const data = await apiPost(`/reports/send-engagement?period=${period}`, {}, token);
      const recipients = (data.recipients || []).join(", ");
      setResult({ type: "success", message: `${period === "monthly" ? "Monthly" : "Semester"} report sent to: ${recipients}` });
      // Refresh archive after sending
      loadArchive();
    } catch (err) {
      setResult({ type: "error", message: `Failed to send: ${err.message}` });
    } finally {
      setSending(null);
    }
  };

  const handlePreview = async (reportId) => {
    if (previewHtml && previewLoading === reportId) {
      setPreviewHtml(null);
      setPreviewLoading(null);
      return;
    }
    setPreviewLoading(reportId);
    try {
      const data = await apiGet(`/reports/archive/${reportId}`, token);
      setPreviewHtml(data.report_html);
    } catch (err) {
      console.error("Failed to load report:", err);
    }
  };

  const handleDownloadPdf = async (reportId) => {
    try {
      const res = await fetch(`${API_BASE}/v1/reports/archive/${reportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Relay_Report_${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF:", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>
        Engagement Reports
      </h3>

      <div style={{ display: "flex", gap: 12 }}>
        <button
          onClick={() => handleSend("monthly")}
          disabled={sending !== null}
          style={{
            padding: "10px 24px",
            borderRadius: 8,
            border: "none",
            backgroundColor: COLORS.accent,
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: sending ? "wait" : "pointer",
            opacity: sending === "monthly" ? 0.7 : 1,
            transition: "opacity 0.15s",
          }}
        >
          {sending === "monthly" ? "Sending..." : "Send Monthly Report"}
        </button>
        <button
          onClick={() => handleSend("semester")}
          disabled={sending !== null}
          style={{
            padding: "10px 24px",
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.surface,
            color: COLORS.text,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: sending ? "wait" : "pointer",
            opacity: sending === "semester" ? 0.7 : 1,
            transition: "opacity 0.15s",
          }}
        >
          {sending === "semester" ? "Sending..." : "Send Semester Report"}
        </button>
      </div>

      {result && (
        <div style={{
          padding: "10px 14px",
          borderRadius: 8,
          marginTop: 12,
          backgroundColor: result.type === "success" ? COLORS.greenLight : COLORS.redLight,
          color: result.type === "success" ? COLORS.green : COLORS.red,
          fontSize: 13,
        }}>
          {result.message}
        </div>
      )}

      {/* Report Archive */}
      <div style={{ marginTop: 24 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>
          Report Archive
        </h4>

        {archiveLoading ? (
          <p style={{ fontSize: 13, color: COLORS.textMuted }}>Loading...</p>
        ) : archive.length === 0 ? (
          <p style={{ fontSize: 13, color: COLORS.textMuted }}>
            No reports sent yet. Send your first report above.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {archive.map((report) => (
              <div
                key={report.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.surface,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    backgroundColor: report.period === "monthly" ? COLORS.accentLight : COLORS.orangeLight,
                    color: report.period === "monthly" ? COLORS.accent : COLORS.orange,
                  }}>
                    {report.period === "monthly" ? "Monthly" : "Semester"}
                  </span>
                  <span style={{ fontSize: 13, color: COLORS.text, fontWeight: 500 }}>
                    {report.period_start} to {report.period_end}
                  </span>
                  <span style={{ fontSize: 12, color: COLORS.textMuted }}>
                    Sent {formatDate(report.created_at)}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => handlePreview(report.id)}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 6,
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.surface,
                      color: COLORS.textSecondary,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      cursor: "pointer",
                    }}
                  >
                    {previewLoading === report.id && previewHtml ? "Close" : "View"}
                  </button>
                  <button
                    onClick={() => handleDownloadPdf(report.id)}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: COLORS.accent,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      cursor: "pointer",
                    }}
                  >
                    PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Report Preview */}
        {previewHtml && (
          <div style={{
            marginTop: 16,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            overflow: "hidden",
            backgroundColor: "#fff",
          }}>
            <div
              dangerouslySetInnerHTML={{ __html: previewHtml }}
              style={{ padding: 0 }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
